import React, {
  createContext,
  useCallback,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { toastError, toastSuccess } from "../../utils";

import { BASE_URL, API_V1_URL } from "../../shared/constants/env";
import { RECOMMENDATIONS_API_TYPES } from "./recommendationsAPITypes";
import { RECOMMENDATION_API_CONSTANTS } from "./constants";

import { recommendationsAPIInitialState } from "./recommendationsAPIInitialState";
import { RecommendationsAPIReducer } from "./recommendationsAPIReducer";

import AppContext from "../appContext";

import { AttachmentSelectorModal } from "../../components/AI/Modals/AttachmentSelectorModal/AttachmentSelectorModal";
import { NoAttachmentsToProcessModalOpen } from "../../components/AI/Modals/NoAttachmentsToProcessModal/NoAttachmentsToProcessModal";
import { ReprocessModal } from "../../components/AI/Modals/ReprocessModal/ReprocessModal";
import { ProcessStatusModal } from "../../components/AI/Modals/ProcessStatusModal/ProcessStatusModal";
import { getFileExtensionFromFileName } from "../../shared/utils/getFileExtensionFromFileName";
import { FloatingProcessStatusCard } from "../../components/AI/Floating/FloatingProcessStatusCard/FloatingProcessStatusCard";
import {
  deleteService,
  getService,
  postService,
} from "../../services/axios/services";
import { getIdToken } from "../../shared/constants/login-store";
import { ProcessResultModal } from "../../components/AI/Modals/ProcessResultModal/ProcessResultModal";
import { emitProjectSheetInvalidation } from "../../shared/utils/projectSheetInvalidation";

export const RecommendationsAPIContext = createContext();

export function RecommendationsAPIProvider({ children }) {
  const workflowStatusControllerRef = useRef(null);
  const contentIdRef = useRef(null);

  const { setCurrentStepBox, current } = React.useContext(AppContext);

  const [state, dispatch] = useReducer(
    RecommendationsAPIReducer,
    recommendationsAPIInitialState
  );

  const ALLOWED_ATTACHMENT_TYPES_TO_CHOOSE_FOR_PLOT_AI_PROCESSING = [
    1212, 1209, 1205, 1178, 1210, 18832, 18738, 1179,
  ];

  const ALLOWED_ATTACHMENT_EXTENSIONS_TO_CHOOSE_FOR_PLOT_AI_PROCESSING = [
    "pdf",
    "txt",
    "doc",
    "docx",
  ];

  const FINISHED_TASK_STATE_ENUM = [
    RECOMMENDATION_API_CONSTANTS.TASK_STATUS_COMPLETE,
    RECOMMENDATION_API_CONSTANTS.TASK_STATUS_FAILED,
    RECOMMENDATION_API_CONSTANTS.TASK_STATUS_CANCELLED,
  ];

  const handleAttachmemtsListForSelectorModal = useCallback(
    (list) => {
      if (!list) return null;

      return list?.filter(
        (attachment) =>
          ALLOWED_ATTACHMENT_TYPES_TO_CHOOSE_FOR_PLOT_AI_PROCESSING.includes(
            attachment.type.id
          ) &&
          ALLOWED_ATTACHMENT_EXTENSIONS_TO_CHOOSE_FOR_PLOT_AI_PROCESSING.includes(
            getFileExtensionFromFileName(attachment.fileName)
          )
      );
    },
    [
      ALLOWED_ATTACHMENT_TYPES_TO_CHOOSE_FOR_PLOT_AI_PROCESSING,
      ALLOWED_ATTACHMENT_EXTENSIONS_TO_CHOOSE_FOR_PLOT_AI_PROCESSING,
    ]
  );

  const setCloseModal = useCallback(
    () =>
      dispatch({
        type: RECOMMENDATIONS_API_TYPES.SET_CLOSE_MODAL,
      }),
    []
  );

  const handleCloseProcessStatusModal = () => {
    setCloseModal();
    if (
      state.processData?.status ===
      RECOMMENDATION_API_CONSTANTS.TASK_STATUS_PENDING
    ) {
      setShowProcessStatusCard(true);
    }
  };

  const setOpenAttachmentSelectorModal = useCallback(
    ({ type, isModalOpen, attachmentsList, contentId }) => {
      dispatch({
        type: RECOMMENDATIONS_API_TYPES.SET_OPEN_ATTACHMENT_SELECTOR_MODAL,
        payload: {
          type,
          isModalOpen,
          attachmentsList:
            handleAttachmemtsListForSelectorModal(attachmentsList),
          contentId,
        },
      });
    },
    [handleAttachmemtsListForSelectorModal]
  );

  const plotRecommendationFlow = useCallback(
    ({ type, data }) => {
      const filteredAttachments = handleAttachmemtsListForSelectorModal(
        data.attachments
      );

      const modalToOpen = data.haveDataFilled
        ? RECOMMENDATION_API_CONSTANTS.REPROCESS_MODAL
        : filteredAttachments?.length
          ? RECOMMENDATION_API_CONSTANTS.SELECTOR_MODAL
          : RECOMMENDATION_API_CONSTANTS.NO_ATTACHMENTS_MODAL;

      setOpenAttachmentSelectorModal({
        type,
        isModalOpen: modalToOpen,
        attachmentsList: filteredAttachments,
        contentId: data.contentId,
      });
    },
    [handleAttachmemtsListForSelectorModal, setOpenAttachmentSelectorModal]
  );

  const setChangeModal = useCallback(
    ({ changeTo }) =>
      dispatch({
        type: RECOMMENDATIONS_API_TYPES.SET_CHANGE_MODAL,
        payload: changeTo,
      }),
    []
  );

  const setStartRecommendationFlow = useCallback(
    ({ type, data }) => {
      if (type === "PLOT") return plotRecommendationFlow({ type, data });
    },
    [plotRecommendationFlow]
  );

  const setEditProcessData = useCallback(({ data }) => {
    dispatch({
      type: RECOMMENDATIONS_API_TYPES.SET_EDIT_PROCESS_DATA,
      payload: { data },
    });
  }, []);

  const setStopCurrentProcessing = useCallback(({ taskState }) => {
    dispatch({
      type: RECOMMENDATIONS_API_TYPES.SET_STOP_CURRENT_PROCESSING,
      payload: taskState,
    });
  }, []);

  const setShowProcessStatusCard = useCallback((bool) => {
    dispatch({
      type: RECOMMENDATIONS_API_TYPES.SET_SHOW_PROCESS_STATUS_CARD,
      payload: bool,
    });
  }, []);

  const abortWorkflowStatusConnection = useCallback(() => {
    if (workflowStatusControllerRef.current) {
      workflowStatusControllerRef.current.abort();
      workflowStatusControllerRef.current = null;
      console.log("SSE connection aborted by navigation.");
    }
  }, []);

  const getWorkflowStatusByContentId = useCallback(
    (contentId) => {
      abortWorkflowStatusConnection();
      const controller = new AbortController();
      workflowStatusControllerRef.current = controller;
      contentIdRef.current = contentId;
      const url = `${BASE_URL}${API_V1_URL}/workflows/statusSSE?contentId=${contentId}`;

      let xCorrelationId = "";

      fetchEventSource(url, {
        signal: controller.signal,
        headers: {
          Authorization: getIdToken(),
        },
        onopen(res) {
          console.log("SSE onopen response: ", res);
          xCorrelationId = res.headers.get("x-correlation-id");

          if (res.ok) {
            console.log("SSE connection established");
          } else {
            console.error("SSE failed to connect:", res.statusText);
            throw new Error("Connection failed");
          }
        },
        onmessage(event) {
          console.log("SSE raw event:", event);

          const taskData = JSON.parse(event.data);
          console.log("SSE message:", taskData);

          if (!Object.keys(JSON.parse(event.data)).length) {
            controller.abort();
            workflowStatusControllerRef.current = null;
            console.log("SSE connection closed after finishing task.");
          }

          const taskState = taskData?.Tasks[0]?.State;

          setEditProcessData({
            data: {
              xCorrelationId,
              status: taskState,
              correlationId: taskData?.CorrelationId,
              taskData: {
                ...taskData.Tasks[0],
                fileName: taskData?.KnowledgeSource?.[0],
              },
            },
          });

          if (taskState === RECOMMENDATION_API_CONSTANTS.TASK_STATUS_PENDING) {
            if (!state.isModalOpen && !state.showProcessStatusCard)
              setShowProcessStatusCard(true);
          }

          if (FINISHED_TASK_STATE_ENUM.includes(taskState)) {
            setStopCurrentProcessing({ taskState });
            if (
              taskState === RECOMMENDATION_API_CONSTANTS.TASK_STATUS_COMPLETE
            ) {
              emitProjectSheetInvalidation();
            }

            if (!state.isModalOpen && !state.showProcessStatusCard)
              setShowProcessStatusCard(true);

            controller.abort();
            workflowStatusControllerRef.current = null;
            console.log("SSE connection closed after finishing task.");
          }
        },
        onerror(err) {
          console.error("SSE error:", err);

          controller.abort();
          workflowStatusControllerRef.current = null;
          throw err;
        },
      });
    },
    [
      FINISHED_TASK_STATE_ENUM,
      setEditProcessData,
      setStopCurrentProcessing,
      setShowProcessStatusCard,
      state.isModalOpen,
      state.showProcessStatusCard,
      abortWorkflowStatusConnection,
    ]
  );

  const postCreateWorkflowByAttachmentId = useCallback(
    ({ attachmentData }) => {
      postService({
        endpoint: `Plots/discovery`,
        postData: {
          attachmentId: attachmentData.id,
        },
      })
        .then(() => {
          dispatch({
            type: RECOMMENDATIONS_API_TYPES.SET_ADD_PROCESS_DATA,
            payload: {
              status: "Pending",
              contentId: state?.contentId,
              data: attachmentData,
            },
          });
          setCurrentStepBox("");

          setChangeModal({ changeTo: "PROCESS-STATUS-MODAL" });

          getWorkflowStatusByContentId(state?.contentId);
        })
        .catch(() => {
          dispatch({
            type: RECOMMENDATIONS_API_TYPES.SET_ADD_PROCESS_DATA,
            payload: { contentId: null, data: null },
          });
          toastError(
            "Ocorreu um erro ao iniciar a leitura do arquivo, por favor tente novamente."
          );
        });
    },
    [
      setChangeModal,
      state.contentId,
      getWorkflowStatusByContentId,
      setCurrentStepBox,
    ]
  );

  const getWorkflowOutput = useCallback(async ({ correlationId, type }) => {
    const params = {};

    if (correlationId) params.correlationId = correlationId;
    if (type) params.type = type;

    const response = await getService({
      endpoint: `workflows/output`,
      params: params,
    });

    if (!response) {
      return { noContent: true };
    }

    return response;
  }, []);

  const handleDiscardWorkflow = useCallback(async () => {
    const correlationId = state.processData?.correlationId;

    if (!correlationId) {
      toastError("Não foi possível descartar o processamento.");
      return;
    }

    try {
      await deleteService({
        endpoint: `workflows/${correlationId}`,
      });

      toastSuccess("Processamento descartado com sucesso.");
    } catch (error) {
      console.error("Erro ao descartar workflow:", error);
      toastError("Erro ao descartar o processamento.");
    } finally {
      setShowProcessStatusCard(false);
      setCloseModal();
      setCurrentStepBox("closed");
      resetWorkflowState();
    }
  }, [
    state.processData?.correlationId,
    abortWorkflowStatusConnection,
    setCloseModal,
    setShowProcessStatusCard,
    setCurrentStepBox,
  ]);

  const setCurrentContent = useCallback(({ contentId }) => {
    dispatch({
      type: RECOMMENDATIONS_API_TYPES.SET_CURRENT_CONTENT,
      payload: contentId,
    });
  }, []);

  const setAddProcessData = useCallback(
    ({ data }) => {
      dispatch({
        type: RECOMMENDATIONS_API_TYPES.SET_ADD_PROCESS_DATA,
        payload: {
          status: RECOMMENDATION_API_CONSTANTS.TASK_STATUS_STARTING,
          data: null,
        },
      });

      postCreateWorkflowByAttachmentId({
        attachmentData: data,
      });
    },
    [postCreateWorkflowByAttachmentId]
  );

  const handleWorkflowTaskNames = (taskName) => {
    const tasks = {
      [RECOMMENDATION_API_CONSTANTS.PLOT_WORKFLOW]: "plot",
    };

    return tasks[taskName] || null;
  };

  const handleCloseProcessResultModal = useCallback(() => {
    setCloseModal();
    setShowProcessStatusCard(true);
    setCurrentStepBox("closed");
  }, [setCloseModal, setShowProcessStatusCard, setCurrentStepBox]);

  const resetWorkflowState = useCallback(() => {
    dispatch({ type: RECOMMENDATIONS_API_TYPES.RESET_WORKFLOW_STATE });
  }, []);

  const handleOnComplete = async () => {
    setShowProcessStatusCard(false);
    setCloseModal();

    try {
      await fetchAndSetWorkflowOutput({
        correlationId: state.processData?.correlationId,
        taskName: state.processData?.taskData?.Name,
      });

      setCurrentStepBox("");
      setChangeModal({
        changeTo: RECOMMENDATION_API_CONSTANTS.PROCESS_RESULT_MODAL,
      });
    } catch {
      toastError("Erro ao carregar o resultado do processamento.");
    }
  };

  const fetchAndSetWorkflowOutput = useCallback(
    async ({ correlationId, taskName }) => {
      const taskType = handleWorkflowTaskNames(taskName) ?? "";

      const output = await getWorkflowOutput({
        correlationId,
        type: taskType,
      });

      if (output?.noContent) {
        dispatch({
          type: RECOMMENDATIONS_API_TYPES.SET_WORKFLOW_NO_CONTENT,
        });
        return { noContent: true };
      }

      dispatch({
        type: RECOMMENDATIONS_API_TYPES.SET_WORKFLOW_OUTPUT,
        payload: output,
      });

      return output;
    },
    [getWorkflowOutput]
  );

  const AI_BUTTON_DISABLED_STATUSES = [
    RECOMMENDATION_API_CONSTANTS.TASK_STATUS_STARTING,
    RECOMMENDATION_API_CONSTANTS.TASK_STATUS_PENDING,
  ];

  const hasRunningAIProcess = AI_BUTTON_DISABLED_STATUSES.includes(
    state.processData?.status
  );

  const providerMemo = useMemo(
    () => ({
      ...state,
      hasRunningAIProcess,
      setCloseModal,
      setChangeModal,
      setStartRecommendationFlow,
      setOpenAttachmentSelectorModal,
      setAddProcessData,
      postCreateWorkflowByAttachmentId,
      getWorkflowOutput,
      fetchAndSetWorkflowOutput,
      handleDiscardWorkflow,
      setCurrentContent,
      getWorkflowStatusByContentId,
      abortWorkflowStatusConnection,
      setShowProcessStatusCard,
      resetWorkflowState,
    }),
    [
      state,
      hasRunningAIProcess,
      setCloseModal,
      setChangeModal,
      setStartRecommendationFlow,
      setOpenAttachmentSelectorModal,
      setAddProcessData,
      postCreateWorkflowByAttachmentId,
      getWorkflowOutput,
      handleDiscardWorkflow,
      fetchAndSetWorkflowOutput,
      setCurrentContent,
      getWorkflowStatusByContentId,
      abortWorkflowStatusConnection,
      setShowProcessStatusCard,
      resetWorkflowState,
    ]
  );

  return (
    <RecommendationsAPIContext.Provider value={providerMemo}>
      {state.isModalOpen === RECOMMENDATION_API_CONSTANTS.SELECTOR_MODAL && (
        <AttachmentSelectorModal
          open={
            state.isModalOpen === RECOMMENDATION_API_CONSTANTS.SELECTOR_MODAL
          }
          attachments={state.attachmentsList}
          title={`Escolha o arquivo onde a IA deva procurar por tramas`}
        />
      )}

      {state.isModalOpen ===
        RECOMMENDATION_API_CONSTANTS.NO_ATTACHMENTS_MODAL && (
        <NoAttachmentsToProcessModalOpen
          open={
            state.isModalOpen ===
            RECOMMENDATION_API_CONSTANTS.NO_ATTACHMENTS_MODAL
          }
          title={`Escolha o arquivo onde a IA deva procurar por tramas`}
        />
      )}

      {state.isModalOpen === RECOMMENDATION_API_CONSTANTS.REPROCESS_MODAL && (
        <ReprocessModal
          open={
            state.isModalOpen === RECOMMENDATION_API_CONSTANTS.REPROCESS_MODAL
          }
          attachments={state.attachmentsList}
          title={`Substituir tramas`}
        />
      )}

      {state.isModalOpen ===
        RECOMMENDATION_API_CONSTANTS.PROCESS_STATUS_MODAL && (
        <ProcessStatusModal
          open={
            state.isModalOpen ===
            RECOMMENDATION_API_CONSTANTS.PROCESS_STATUS_MODAL
          }
          handleClose={handleCloseProcessStatusModal}
          handleOnComplete={handleOnComplete}
          title={`Leitura do arquivo`}
        />
      )}

      {state.isModalOpen ===
        RECOMMENDATION_API_CONSTANTS.PROCESS_RESULT_MODAL && (
        <ProcessResultModal
          open={
            state.isModalOpen ===
            RECOMMENDATION_API_CONSTANTS.PROCESS_RESULT_MODAL
          }
          data={state.workflowOutput}
          contentId={contentIdRef.current}
          onDiscard={handleDiscardWorkflow}
          onClose={handleCloseProcessResultModal}
          setCloseModal={setCloseModal}
          workflowNoContent={state.workflowNoContent}
        />
      )}

      {state.showProcessStatusCard &&
        (!current.step || current.step === "closed") && (
          <FloatingProcessStatusCard handleOnComplete={handleOnComplete} />
        )}

      {children}
    </RecommendationsAPIContext.Provider>
  );
}
